import { useState, FormEvent, useEffect } from "react";
import { useSharedState } from "state";
import ModalComponent from "components/modal";
import Select, { components, GroupProps, MultiValue } from "react-select";
import { downloadBudgetReport, generateBudgetReport } from "api/budget_request";
import moment from "moment";
import { BudgetReport, ExcelPayload } from "types/report";

type FormErrors = Record<string, boolean>;

type FormRecords = {
  company_id: number;
  department_id: number;
  report_template_index: number;
  year: number;
};

type SelectOption = {
  value: number;
  label: string;
};

const REQUIRED_FIELDS = ["company_id", "department_id", "report_template_index", "year"] as const;

const getInputErrorMessage = (fieldId: string) =>
  fieldId === "file" ? "Добавьте договор" : "Введите корректное значение";

export default function NewReport() {
  const {
    departments,
    getDepartmentsData,
    companies,
    getCompaniesData,
    notify,
    setReport,
    setReportDetails,
    reportTemplates,
    setReportType,
    setExcelName,
    getReportTemplatesData,
    isAdmin,
  } = useSharedState();

  const [formRecords, setFormRecords] = useState<FormRecords>({
    company_id: 0,
    department_id: 0,
    report_template_index: -1,
    year: moment().year(),
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  useEffect(() => {
    getDepartmentsData();
    getCompaniesData();
    getReportTemplatesData();
  }, []);

  const budgetFields = [
    {
      id: "company_id",
      title: "Юр. лицо",
      type: "select",
      placeholder: "Выберите юр. лицо",
      options: companies
        .filter((com) => com.status)
        .map((com) => ({
          label: com.company,
          value: com.company_id,
        })),
      isMulti: false,
    },
    {
      id: "department_id",
      title: "Отдел",
      type: "select",
      placeholder: "Выберите отдел",
      options: isAdmin
        ? [
            {
              label: "Все отделы",
              options: departments
                .filter((dep) => dep.status)
                .map((dep) => ({
                  label: dep.department,
                  value: dep.department_id,
                })),
            },
          ]
        : departments
            .filter((dep) => dep.status)
            .map((dep) => ({
              label: dep.department,
              value: dep.department_id,
            })),
      isMulti: isAdmin,
    },
    {
      id: "report_template_index",
      title: "Шаблон отчёта",
      type: "select",
      placeholder: "Выберите шаблон",
      options: reportTemplates
        .filter((rep) => rep.status)
        .map((rep, index) => ({
          label: rep.template_name,
          value: index + 1,
        })),
      isMulti: false,
    },
    {
      id: "year",
      title: "Год",
      type: "number",
      placeholder: undefined,
      options: undefined,
      isMulti: undefined,
    },
  ] as const;

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const value = formRecords[field];
      if (Array.isArray(value) ? value.length === 0 : !value || !value?.toString().trim()) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;
  type SelectChangeEvent = readonly SelectOption[] | null;
  type SingleSelectEvent = SelectOption | null;

  const handleInputChange = (e: InputChangeEvent | SelectChangeEvent | SingleSelectEvent, fieldId?: string) => {
    if (e && "target" in e) {
      // Handle input change
      const { name, value } = e.target;
      setFormRecords((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: false }));
    } else {
      // Handle select change
      // @ts-ignore
      const values = Array.isArray(e) ? e.map((option) => option.value) : e ? e.value : "";
      setFormRecords((prev) => ({ ...prev, [fieldId!]: values }));
      setErrors((prev) => ({ ...prev, [fieldId!]: false }));
    }
  };

  const generateReport = async () => {
    setFormLoading(true);
    try {
      const description = reportTemplates[formRecords.report_template_index - 1].json_description;
      const payload = {
        year: formRecords.year,
        structure: description.structure,
        add_forecast: description.add_forecast,
        groupings: description.groupings,
        company_id: [formRecords.company_id],
        department_id: Array.isArray(formRecords.department_id)
          ? formRecords.department_id
          : formRecords.department_id === 9999
          ? undefined
          : formRecords.department_id === 0
          ? undefined
          : [formRecords.department_id],
        need_columns: description.add_forecast
          ? [...description.need_columns].concat("forecast_value")
          : description.need_columns,
        additional_formulas: description.additional_formulas,
        need_formulas: description.need_formulas,
        generate_excel: true,
      };
      const res: BudgetReport = await generateBudgetReport(payload);

      return { res, payload };
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при формировании отчёта");
      setFormLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!validateForm()) {
      notify("Заполните все поля", "danger");
      return;
    }

    setFormLoading(true);
    try {
      const localResponse = await generateReport();

      const payload: ExcelPayload = {
        file_name: localResponse?.res.file_name!,
        file_path: localResponse?.res.file_path!,
      };
      const res: any = await downloadBudgetReport(payload);

      const localName = reportTemplates[formRecords.report_template_index - 1].template_name;
      const fileName = `${localName} ${moment().format("DD.MM.YYYY HH:mm")}.xlsx`;
      const blob = new Blob([res], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName.replaceAll(" ", "_");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      notify("Скачивание завершено", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении файла");
    } finally {
      setFormLoading(false);
    }
  };

  const submitBudget = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      notify("Заполните все поля", "danger");
      return;
    }

    try {
      const localResponse = await generateReport();

      setReport(localResponse?.res!);
      setReportDetails(localResponse?.payload!);
      setReportType(reportTemplates[formRecords.report_template_index - 1].json_description.structure);
      setExcelName(reportTemplates[formRecords.report_template_index - 1].template_name);
      notify("Отчёт сформирован", "success");

      setFormSubmitted(true);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при формировании отчёта");
    } finally {
      setFormLoading(false);
    }
  };

  const addAllDepartments = () => {
    const allDepartmentIds: any = departments.filter((dep) => dep.status).map((dep) => dep.department_id);

    setFormRecords((prev) => ({
      ...prev,
      department_id: allDepartmentIds,
    }));
    setErrors((prev) => ({ ...prev, department_id: false }));
  };

  // Helper to flatten options
  const flattenOptions = (opts: any[] = []) => opts.flatMap((opt) => (opt.options ? opt.options : opt));

  const groupStyles = {
    border: `2px dotted green`,
    borderRadius: "5px",
    background: "#f2fcff",
  };

  const Group = (props: GroupProps<SelectOption, true>) => (
    <div style={groupStyles}>
      <components.Group {...props} />
    </div>
  );

  const renderField = ({ id, title, type, placeholder, options, isMulti }: (typeof budgetFields)[number]) => {
    return (
      <div className={"col-lg-6"} key={id}>
        <div className="mb-3">
          <label className="form-label required">{title}</label>
          <div className="input-icon mb-3">
            {type === "number" ? (
              <input
                type="number"
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                min={1990}
                max={moment().year()}
                step={1}
                value={formRecords[id]}
                onChange={handleInputChange}
                disabled={isFormLoading}
              />
            ) : (
              <Select<SelectOption, boolean>
                name={id}
                placeholder={placeholder}
                value={
                  isMulti
                    ? flattenOptions(options)?.filter((opt: any) =>
                        Array.isArray(formRecords[id])
                          ? // @ts-ignore
                            formRecords[id].includes(opt.value)
                          : formRecords[id] === opt.value
                      )
                    : flattenOptions(options)?.find((opt: any) => opt.value === formRecords[id]) ?? null
                }
                onChange={(newValue) => handleInputChange(newValue, id)}
                isDisabled={isFormLoading}
                options={options}
                components={{
                  Group,
                  GroupHeading: (props) => (
                    <div className="d-flex align-items-center justify-content-between">
                      <components.GroupHeading {...props} />
                      <div className="select-group-header-btn" onClick={addAllDepartments}>
                        Добавить все
                      </div>
                    </div>
                  ),
                }}
                isSearchable
                isMulti={isMulti}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            )}

            <div className="invalid-feedback">{getInputErrorMessage(id)}</div>
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const newBudgetBody = (
    <form id="budgetForm" className="row" onSubmit={submitBudget}>
      {budgetFields.map(renderField)}
    </form>
  );

  const newBudgetFooter = (
    <>
      <button type="button" className="btn btn-primary d-flex gap-1" disabled={isFormLoading} onClick={downloadExcel}>
        Скачать Excel
        {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
      </button>
      <button type="submit" form="budgetForm" className="btn btn-success d-flex gap-1" disabled={isFormLoading}>
        Показать отчёт
        {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
      </button>
    </>
  );

  return (
    <ModalComponent
      header={"Новый отчёт"}
      body={newBudgetBody}
      footer={newBudgetFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
